import { Component, Inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatTabChangeEvent } from '@angular/material/tabs';
import dayjs from 'dayjs';
import {
  BookingDTO,
  SettingApi,
  BookingApi,
  SettingDTO,
  GetSmsPreviewAdminSmsTypeEnum,
} from '@lkp/parking-sdk';
import { SETTING_API, BOOKING_API } from '../../core/providers/api.module';
import { Subject, forkJoin, takeUntil } from 'rxjs';

@Component({
    selector: 'app-sms-settings',
    templateUrl: './sms-settings.component.html',
    styleUrls: ['./sms-settings.component.scss'],
    standalone: false
})
export class SmsSettingsComponent {
  preText = new FormControl<string>('', { nonNullable: true });
  postText = new FormControl<string>('', { nonNullable: true });

  currentSMSPreSetting?: SettingDTO;
  currentSMSPostSetting?: SettingDTO;

  currentSMSType?: GetSmsPreviewAdminSmsTypeEnum =
    GetSmsPreviewAdminSmsTypeEnum.Confirm;
  message: string = '';
  errorPreview = false;

  dummyBooking: BookingDTO = {
    qtyPersons: 2,
    registrationNumber: 'ABC123',
    qr: '123456',
    departureDate: new Date(),
    arrivalDate: dayjs().add(1, 'week').add(1, 'hour').toDate(),
    departureFlight: {
      flightNumber: 'SK456',
      arrivalAirport: 'Riga',
      scheduledDepartureDate: dayjs().add(2, 'hour').toDate(),
    },
    arrivalFlight: {
      flightNumber: 'SK123',
      terminal: 't5',
      departureAirport: 'Arlanda',
      scheduledArrivalDate: dayjs().add(1, 'week').toDate(),
    },
    resource: {
      name: 'PARKING_OUTDOOR',
      label: 'Utomhus',
    },
  };

  postTextChanged: boolean = false;
  preTextChanged: boolean = false;

  readonly onDestroy$ = new Subject<void>();

  constructor(
    @Inject(SETTING_API) readonly settingApi: SettingApi,
    @Inject(BOOKING_API) readonly bookingApi: BookingApi
  ) {
    this.getPreview();
  }

  ngOnInit() {
    /* Listen to changes */
    this.postText.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.postTextChanged = true;
        this.countSMS();
      });
    this.preText.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.preTextChanged = true;
      this.countSMS();
    });

    /* Get settings and populate form */
    this.getSettings();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  tabChanged(event: MatTabChangeEvent) {
    switch (event.tab.textLabel) {
      case 'Bekräftelse':
        this.currentSMSType = GetSmsPreviewAdminSmsTypeEnum.Confirm;
        break;
      case 'Påminnelse':
        this.currentSMSType = GetSmsPreviewAdminSmsTypeEnum.Departure;
        break;
      case 'Hemkomst':
        this.currentSMSType = GetSmsPreviewAdminSmsTypeEnum.Arrival;
        break;

      default:
        this.currentSMSType = GetSmsPreviewAdminSmsTypeEnum.Confirm;
        break;
    }

    this.getPreview();
    this.getSettings();
  }

  getSettings() {
    if (this.currentSMSType === 'CONFIRM') {
      /* Get confirmation settings */
      forkJoin([
        this.settingApi.getSetting({ name: 'SMS_PRE_MESSAGE' }),
        this.settingApi.getSetting({ name: 'SMS_POST_MESSAGE' }),
      ])
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(([pre, post]) => {
          if (pre.value) {
            this.preText.setValue(pre.value, { emitEvent: false });
            this.currentSMSPreSetting = pre;
          }
          if (post.value) {
            this.postText.setValue(post.value, { emitEvent: false });
            this.currentSMSPostSetting = post;
          }
          this.countSMS();
        });
    } else if (this.currentSMSType === 'ARRIVAL') {
      /* Get arrival settings */
      forkJoin([
        this.settingApi.getSetting({ name: 'ARRIVAL_SMS_PRE_MESSAGE' }),
        this.settingApi.getSetting({ name: 'ARRIVAL_SMS_POST_MESSAGE' }),
      ])
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(([pre, post]) => {
          if (pre.value) {
            this.preText.setValue(pre.value, { emitEvent: false });
            this.currentSMSPreSetting = pre;
          }
          if (post.value) {
            this.postText.setValue(post.value, { emitEvent: false });
            this.currentSMSPostSetting = post;
          }
          this.countSMS();
        });
    } else {
      /* Get departure settings */
      forkJoin([
        this.settingApi.getSetting({ name: 'DEPARTURE_SMS_PRE_MESSAGE' }),
        this.settingApi.getSetting({ name: 'DEPARTURE_SMS_POST_MESSAGE' }),
      ])
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(([pre, post]) => {
          if (pre.value) {
            this.preText.setValue(pre.value, { emitEvent: false });
            this.currentSMSPreSetting = pre;
          }
          if (post.value) {
            this.postText.setValue(post.value, { emitEvent: false });
            this.currentSMSPostSetting = post;
          }
          this.countSMS();
        });
    }
  }

  getPreview() {
    this.bookingApi
      .getSmsPreviewAdmin({
        bookingDTO: this.dummyBooking,
        smsType: this.currentSMSType,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((text) => {
        if (text.body) {
          this.message = text.body;
          this.errorPreview = false;
        } else {
          console.error('Error fetching preview: ', text);
          this.errorPreview = true;
        }
        this.countSMS();
      });
  }

  save(field: 'PRE' | 'POST', type: 'CONFIRMATION' | 'ARRIVAL' | 'DEPARTURE') {
    switch (field) {
      /* Update pre setting */
      case 'PRE':
        this.preTextChanged = false;
        if (type === 'CONFIRMATION') {
          this.settingApi
            .updateSetting({
              name: 'SMS_PRE_MESSAGE',
              settingDTO: {
                ...this.currentSMSPreSetting,
                value: this.preText.value,
              },
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((pre) => {
              this.currentSMSPreSetting = pre;
              this.getPreview();
            });
        } else if (type === 'ARRIVAL') {
          this.settingApi
            .updateSetting({
              name: 'ARRIVAL_SMS_PRE_MESSAGE',
              settingDTO: {
                ...this.currentSMSPreSetting,
                value: this.preText.value,
              },
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((pre) => {
              this.currentSMSPreSetting = pre;
              this.getPreview();
            });
        } else if (type === 'DEPARTURE') {
          this.settingApi
            .updateSetting({
              name: 'DEPARTURE_SMS_PRE_MESSAGE',
              settingDTO: {
                ...this.currentSMSPreSetting,
                value: this.preText.value,
              },
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((pre) => {
              this.currentSMSPreSetting = pre;
              this.getPreview();
            });
        }
        break;

      /* Update post setting */
      case 'POST':
        this.postTextChanged = false;
        if (type === 'CONFIRMATION') {
          this.settingApi
            .updateSetting({
              name: 'SMS_POST_MESSAGE',
              settingDTO: {
                ...this.currentSMSPostSetting,
                value: this.postText.value,
              },
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((post) => {
              this.currentSMSPostSetting = post;
              this.getPreview();
            });
        } else if (type === 'ARRIVAL') {
          this.settingApi
            .updateSetting({
              name: 'ARRIVAL_SMS_POST_MESSAGE',
              settingDTO: {
                ...this.currentSMSPostSetting,
                value: this.postText.value,
              },
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((post) => {
              this.currentSMSPostSetting = post;
              this.getPreview();
            });
        } else if (type === 'DEPARTURE') {
          this.settingApi
            .updateSetting({
              name: 'DEPARTURE_SMS_POST_MESSAGE',
              settingDTO: {
                ...this.currentSMSPostSetting,
                value: this.postText.value,
              },
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((post) => {
              this.currentSMSPostSetting = post;
              this.getPreview();
            });
        }
        break;
    }
  }

  reset(field: 'PRE' | 'POST') {
    if (field === 'PRE') {
      this.preTextChanged = false;
      this.preText.setValue(this.currentSMSPreSetting?.value ?? '', {
        emitEvent: false,
      });
    } else {
      this.postTextChanged = false;
      this.postText.setValue(this.currentSMSPostSetting?.value ?? '', {
        emitEvent: false,
      });
    }
  }

  countSMS(): number {
    const totalTextLength = this.message.length;
    return totalTextLength <= 160 ? 1 : Math.ceil(totalTextLength / 153);
  }
}
